import { useMemo } from 'react';

interface CarreiraNivel {
  id: string;
  carreira_id: string;
  nivel: number;
  designacao: string;
  vencimento_base: number | null;
  ordem: number;
}

interface Carreira {
  id: string;
  nome: string;
  ativo: boolean;
  nivel_maximo: number | null;
}

interface CarreirasGraficoProps {
  carreiras: Carreira[];
  niveis: CarreiraNivel[];
}

const CARREIRA_COLORS = [
  { line: '#18181b', fill: 'rgba(24,24,27,0.08)', dot: '#18181b', label: 'text-zinc-800' },
  { line: '#d97706', fill: 'rgba(217,119,6,0.08)', dot: '#d97706', label: 'text-amber-600' },
  { line: '#059669', fill: 'rgba(5,150,105,0.08)', dot: '#059669', label: 'text-emerald-600' },
  { line: '#dc2626', fill: 'rgba(220,38,38,0.08)', dot: '#dc2626', label: 'text-red-600' },
];

export default function CarreirasGrafico({ carreiras, niveis }: CarreirasGraficoProps) {
  const WIDTH = 560;
  const HEIGHT = 260;
  const PAD_LEFT = 72;
  const PAD_RIGHT = 24;
  const PAD_TOP = 20;
  const PAD_BOTTOM = 44;
  const CHART_W = WIDTH - PAD_LEFT - PAD_RIGHT;
  const CHART_H = HEIGHT - PAD_TOP - PAD_BOTTOM;

  // Carreiras com níveis e vencimentos definidos
  const carreirasComNiveis = useMemo(() => {
    return carreiras
      .filter((c) => c.ativo)
      .map((c) => {
        const ns = niveis
          .filter((n) => n.carreira_id === c.id && n.vencimento_base != null)
          .sort((a, b) => a.nivel - b.nivel);
        return { carreira: c, niveis: ns };
      })
      .filter((c) => c.niveis.length >= 2);
  }, [carreiras, niveis]);

  if (carreirasComNiveis.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400">
        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-full">
          <i className="ri-line-chart-line text-2xl" />
        </div>
        <p className="text-sm">Sem dados suficientes para o gráfico</p>
        <p className="text-xs text-slate-300">Adiciona vencimentos base nos níveis de cada carreira</p>
      </div>
    );
  }

  // Calcular escala global
  const allVencimentos = carreirasComNiveis.flatMap((c) => c.niveis.map((n) => Number(n.vencimento_base)));
  const allNiveis = carreirasComNiveis.flatMap((c) => c.niveis.map((n) => n.nivel));
  const minV = Math.floor(Math.min(...allVencimentos) / 100) * 100;
  const maxV = Math.ceil(Math.max(...allVencimentos) / 100) * 100;
  const minN = Math.min(...allNiveis);
  const maxN = Math.max(...allNiveis);

  function xPos(nivel: number): number {
    if (maxN === minN) return PAD_LEFT + CHART_W / 2;
    return PAD_LEFT + ((nivel - minN) / (maxN - minN)) * CHART_W;
  }

  function yPos(venc: number): number {
    if (maxV === minV) return PAD_TOP + CHART_H / 2;
    return PAD_TOP + CHART_H - ((venc - minV) / (maxV - minV)) * CHART_H;
  }

  // Grid horizontal (5 linhas)
  const gridLines = Array.from({ length: 5 }, (_, i) => {
    const v = minV + ((maxV - minV) * i) / 4;
    return { v, y: yPos(v) };
  });

  // Ticks do eixo X (níveis únicos)
  const xTicks = Array.from(new Set(allNiveis)).sort((a, b) => a - b);

  // Tooltip state — usamos SVG nativo com foreignObject para simplicidade
  return (
    <div className="w-full">
      {/* Legenda */}
      <div className="flex flex-wrap gap-4 mb-4 px-1">
        {carreirasComNiveis.map((c, i) => {
          const color = CARREIRA_COLORS[i % CARREIRA_COLORS.length];
          return (
            <div key={c.carreira.id} className="flex items-center gap-2">
              <span
                className="inline-block w-8 h-0.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: color.line }}
              />
              <span className={`text-xs font-medium ${color.label}`}>{c.carreira.nome}</span>
              {c.carreira.nivel_maximo != null && (
                <span className="text-[10px] text-slate-400">máx. {c.carreira.nivel_maximo}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* SVG Chart */}
      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full"
          style={{ minWidth: '320px', maxWidth: '100%' }}
        >
          {/* Grid lines */}
          {gridLines.map(({ v, y }) => (
            <g key={v}>
              <line
                x1={PAD_LEFT}
                y1={y}
                x2={PAD_LEFT + CHART_W}
                y2={y}
                stroke="#f1f5f9"
                strokeWidth={1}
              />
              <text
                x={PAD_LEFT - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#94a3b8"
                fontFamily="monospace"
              >
                {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
              </text>
            </g>
          ))}

          {/* Eixo X ticks */}
          {xTicks.map((nivel) => (
            <g key={nivel}>
              <line
                x1={xPos(nivel)}
                y1={PAD_TOP + CHART_H}
                x2={xPos(nivel)}
                y2={PAD_TOP + CHART_H + 5}
                stroke="#cbd5e1"
                strokeWidth={1}
              />
              <text
                x={xPos(nivel)}
                y={PAD_TOP + CHART_H + 18}
                textAnchor="middle"
                fontSize={10}
                fill="#94a3b8"
              >
                {nivel}
              </text>
            </g>
          ))}

          {/* Label eixo X */}
          <text
            x={PAD_LEFT + CHART_W / 2}
            y={HEIGHT - 4}
            textAnchor="middle"
            fontSize={10}
            fill="#94a3b8"
          >
            Nível
          </text>

          {/* Label eixo Y */}
          <text
            x={12}
            y={PAD_TOP + CHART_H / 2}
            textAnchor="middle"
            fontSize={10}
            fill="#94a3b8"
            transform={`rotate(-90, 12, ${PAD_TOP + CHART_H / 2})`}
          >
            Vencimento (€)
          </text>

          {/* Linhas e pontos por carreira */}
          {carreirasComNiveis.map((c, i) => {
            const color = CARREIRA_COLORS[i % CARREIRA_COLORS.length];
            const points = c.niveis.map((n) => ({
              x: xPos(n.nivel),
              y: yPos(Number(n.vencimento_base)),
              nivel: n.nivel,
              venc: Number(n.vencimento_base),
              designacao: n.designacao,
            }));

            const pathD = points
              .map((p, pi) => `${pi === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
              .join(' ');

            // Área preenchida
            const areaD = points.length > 0
              ? `M ${points[0].x} ${PAD_TOP + CHART_H} L ${points.map((p) => `${p.x} ${p.y}`).join(' L ')} L ${points[points.length - 1].x} ${PAD_TOP + CHART_H} Z`
              : '';

            return (
              <g key={c.carreira.id}>
                {/* Área */}
                {areaD && (
                  <path d={areaD} fill={color.fill} />
                )}
                {/* Linha */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={color.line}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {/* Pontos */}
                {points.map((p) => (
                  <g key={p.nivel}>
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={4}
                      fill="white"
                      stroke={color.dot}
                      strokeWidth={2}
                    />
                    {/* Tooltip ao hover via title */}
                    <title>{`${c.carreira.nome} — ${p.designacao}: ${p.venc.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}`}</title>
                  </g>
                ))}
              </g>
            );
          })}

          {/* Borda do gráfico */}
          <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={PAD_TOP + CHART_H} stroke="#e2e8f0" strokeWidth={1} />
          <line x1={PAD_LEFT} y1={PAD_TOP + CHART_H} x2={PAD_LEFT + CHART_W} y2={PAD_TOP + CHART_H} stroke="#e2e8f0" strokeWidth={1} />
        </svg>
      </div>

      {/* Tabela resumo */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {carreirasComNiveis.map((c, i) => {
          const color = CARREIRA_COLORS[i % CARREIRA_COLORS.length];
          const vencMin = Math.min(...c.niveis.map((n) => Number(n.vencimento_base)));
          const vencMax = Math.max(...c.niveis.map((n) => Number(n.vencimento_base)));
          const progressao = vencMax - vencMin;
          return (
            <div key={c.carreira.id} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color.line }} />
                <span className="text-xs font-semibold text-slate-700 truncate">{c.carreira.nome}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Entrada (N.1)</span>
                  <span className="font-mono font-semibold text-slate-700">{vencMin.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Topo (N.{c.niveis[c.niveis.length - 1].nivel})</span>
                  <span className="font-mono font-semibold text-slate-700">{vencMax.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}</span>
                </div>
                <div className="flex justify-between text-xs pt-1 border-t border-slate-200">
                  <span className="text-slate-400">Progressão total</span>
                  <span className="font-mono font-semibold text-emerald-600">+{progressao.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
